import { useMemo } from 'react';
import Editor from '@monaco-editor/react';
import { Box, CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PropTypes from 'prop-types';

// Monaco, as the scraper scenario editor uses it. @monaco-editor/react loads the
// editor itself from its default CDN on first use, so nothing of Monaco is in the
// app bundle; the cabinet sets no CSP that would stop it.
//
// Not src/components/code-editor, which despite its name is the rule builder.

const BASE_OPTIONS = {
    fontSize: 13,
    tabSize: 2,
    minimap: { enabled: false },
    // The overview ruler beside the scrollbar drew the cursor's position as a
    // dark tick in the top-right corner of every editor, read as a rendering
    // glitch. With no minimap it shows nothing else worth the column.
    overviewRulerLanes: 0,
    hideCursorInOverviewRuler: true,
    overviewRulerBorder: false,
    scrollBeyondLastLine: false,
    // Re-measures when the box resizes — the panes are flex children of the page,
    // which follows the window.
    automaticLayout: true,
    // Suggestions and hovers are drawn outside the box's overflow: hidden.
    fixedOverflowWidgets: true
};

/**
 * `path` names the editor's model. Several editors share one Monaco instance, so
 * each needs its own — and a scenario's id in it keeps two scenarios' models
 * apart. The extension does not choose the language; `language` does.
 * `onMount(editor, monaco)` hands out the editor, for decorations and edits.
 */
export default function CodeEditor({ language, path, value, onChange, beforeMount, onMount, readOnly, ariaLabel, options, sx }) {
    const theme = useTheme();
    // Kept stable between renders: the wrapper pushes a changed options object to
    // the editor, and this one would otherwise change on every keystroke.
    const editorOptions = useMemo(
        () => ({ ...BASE_OPTIONS, readOnly: Boolean(readOnly), ariaLabel, ...options }),
        [readOnly, ariaLabel, options]
    );

    return (
        <Box
            sx={{
                minHeight: 0,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                overflow: 'hidden',
                ...sx
            }}
        >
            <Editor
                height="100%"
                language={language}
                path={path}
                value={value}
                theme={theme.palette.mode === 'dark' ? 'vs-dark' : 'light'}
                beforeMount={beforeMount}
                onMount={onMount}
                onChange={(next) => onChange?.(next ?? '')}
                loading={<CircularProgress size={24} />}
                options={editorOptions}
            />
        </Box>
    );
}

CodeEditor.propTypes = {
    language: PropTypes.string.isRequired,
    path: PropTypes.string.isRequired,
    value: PropTypes.string,
    onChange: PropTypes.func,
    beforeMount: PropTypes.func,
    onMount: PropTypes.func,
    readOnly: PropTypes.bool,
    ariaLabel: PropTypes.string,
    options: PropTypes.object,
    sx: PropTypes.object
};
